// Besoins en bénévoles des matchs, par équipe (D-119) : la logique pure de Club › Bénévolat › Besoins des matchs (D-127) et des fiches
// du bureau, la même que celle de la base (supabase/migrations/20261102300000_besoins_matchs.sql) :
//  - ce qu'un match à domicile demande : les besoins du club, puis les règles de son équipe engagée (« Club · n »,
//    « Autre », « Aucun », un besoin pour elle seule), puis la condition « 2 matchs ou plus au gymnase ce jour-là » ;
//  - les puces qui résument une équipe (« Comme le club », « Buvette : 1 », « Pas d'arbitre ») et ce qui change ;
//  - la suggestion selon le niveau (régional et au-dessus : pas d'arbitre ; mini-basket : ni arbitre ni 24 secondes) ;
//  - les messages qui partent tout seuls (« Le bureau t'a inscrit… », « Merci Karim ! 🙏 ») : la base écrit les mêmes,
//    vérifié des deux côtés sur scripts/bench/besoins-cases.json ;
//  - ce qu'une personne fait déjà ce jour-là (la fiche « Inscrire quelqu'un »).
// Tutoiement, vouvoiement d'un compte partenaire seul ; accords au genre et au nombre du besoin (« la buvette est
// assurée », « les 24 secondes sont assurées ») ; apostrophes typographiques, comme les maquettes.
import type { LevelKey } from './rt';

export type RuleMode = 'club' | 'count' | 'none';
/** Un besoin du club pour chaque match à domicile. */
export interface ClubNeed { label: string; needed: number }
/** Une règle d'une équipe engagée : un besoin du club réglé autrement, ou un besoin pour elle seule (`extra`). */
export interface UnitRule { label: string; mode: RuleMode; needed: number | null; only_multi: boolean; extra: boolean; position: number }
export interface PlannedNeed { label: string; needed: number; wanted: boolean; conditional: boolean; extra: boolean }

/** Sans réglage du club, la base crée ces trois besoins (D-026). */
export const DEFAULT_NEEDS: ClubNeed[] = [{ label: 'Table de marque', needed: 1 }, { label: 'Chronomètre', needed: 1 }, { label: 'Buvette', needed: 2 }];
/** Les besoins proposés à l'ajout. */
export const NEED_LABELS = ['Table de marque', 'Chronomètre', '24 secondes', 'Buvette', 'Arbitre', 'Accueil', 'E-marque', 'Installation', 'Rangement', 'Gâteaux', 'Photos'];

export const norm = (s: string) => s.trim().toLowerCase();
export const sameLabel = (a: string, b: string) => norm(a) === norm(b);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** La règle d'une équipe pour un besoin du club (celle qui le règle, avant un besoin du même nom pour elle seule). */
export const ruleOf = (rules: UnitRule[], label: string): UnitRule | undefined =>
  rules.filter(r => sameLabel(r.label, label)).sort((a, b) => Number(a.extra) - Number(b.extra))[0];

/** Ce qu'un match à domicile demande (comme _volunteer_plan) : `multi` = 2 matchs ou plus au gymnase ce jour-là. */
export function planFor(club: ClubNeed[], rules: UnitRule[], multi: boolean): PlannedNeed[] {
  const out: PlannedNeed[] = club.map(c => {
    const r = ruleOf(rules, c.label);
    const mode = r?.mode ?? 'club', cond = r?.only_multi ?? false;
    return { label: c.label, needed: mode === 'count' ? (r?.needed ?? c.needed) : c.needed, wanted: mode !== 'none' && (!cond || multi), conditional: cond, extra: false };
  });
  for (const r of rules.filter(x => x.extra && !club.some(c => sameLabel(c.label, x.label))).sort((a, b) => a.position - b.position)) {
    out.push({ label: r.label, needed: r.needed ?? 1, wanted: !r.only_multi || multi, conditional: r.only_multi, extra: true });
  }
  return out;
}

/** Les règles à garder en base : « Club · n » sans condition ne s'écrit pas (c'est l'absence de règle). */
export const storedRules = (rules: UnitRule[]): UnitRule[] =>
  rules.filter(r => r.extra || r.mode !== 'club' || r.only_multi).map((r, i) => ({ ...r, needed: r.mode === 'count' ? r.needed ?? 1 : null, position: i }));

// -----------------------------------------------------------------------------
// Le genre et le nombre d'un besoin (« la buvette », « au chronomètre », « aux 24 secondes », « pas d'arbitre »)
// -----------------------------------------------------------------------------
const FEM = new Set(['table', 'buvette', 'cuisine', 'caisse', 'e-marque', 'marque', 'entrée', 'billetterie', 'sono', 'tenue', 'vente', 'crêpe',
  'boisson', 'permanence', 'tombola', 'plonge', 'garderie', 'navette', 'voiture', 'collecte', 'photo', 'seconde', 'minute', 'feuille', 'salle',
  'sortie', 'musique', 'pâtisserie', 'friterie', 'réception', 'sonorisation', 'feuille de match']);
const FEM_END = /(tion|sion|ette|ure|ée|ie|té|ance|ence|ade|ise|elle|esse|ine)$/;
const SING_S = new Set(['repas', 'bus', 'accès', 'gaz', 'prix', 'choix', 'tapis', 'temps', 'corps', 'os', 'jus']);
const VOWEL = /^[aeiouyàâäéèêëîïôöûùüœæ]/i;
/** Un besoin qui est une personne (« arbitre ») : on est inscrit « comme arbitre », pas « à l'arbitre ». */
const PERSON = /^(arbitre|chronométreur|chronométreuse|marqueur|marqueuse|délégué|déléguée|speaker|photographe|animateur|animatrice|officiel|officielle)s?$/;
/** « Table de marque » → « table de marque » ; « SF1 » reste « SF1 » (comme _lower_first en base). */
export const lowerFirst = (s: string) => (/^[A-ZÀ-ÖØ-Þ][a-zà-öø-ÿ]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** Le genre et le nombre d'un besoin, d'après son premier nom (« table de marque » : la table ; « 24 secondes » : les secondes). */
const headOf = (label: string) => { const words = norm(label).split(/\s+/); return (/^\d+$/.test(words[0] ?? '') ? words[1] : words[0]) ?? ''; };
export function noun(label: string): { fem: boolean; plural: boolean } {
  const head = headOf(label);
  const plural = /[sx]$/.test(head) && !SING_S.has(head);
  const sing = plural ? head.slice(0, -1) : head;
  return { fem: FEM.has(head) || FEM.has(sing) || FEM_END.test(sing), plural };
}

export interface LabelForms { le: string; a: string; pas: string; fem: boolean; plural: boolean }
export function labelForms(label: string): LabelForms {
  const l = lowerFirst(label.trim());
  const { fem, plural } = noun(label);
  const v = VOWEL.test(l);
  return {
    le: plural ? `les ${l}` : v ? `l’${l}` : fem ? `la ${l}` : `le ${l}`,
    a: PERSON.test(headOf(label)) ? `comme ${l}` : plural ? `aux ${l}` : v ? `à l’${l}` : fem ? `à la ${l}` : `au ${l}`,
    pas: v ? `pas d’${l}` : `pas de ${l}`,
    fem, plural,
  };
}
/** Un participe accordé au besoin : « assurée », « retirés ». */
export const agreeNoun = (label: string, word: string) => { const n = noun(label); return `${word}${n.fem ? 'e' : ''}${n.plural ? 's' : ''}`; };

/** « 24 secondes » → « 24 s » (les puces). */
/** « de SF1 », « d’U15 filles ». */
export const deName = (n: string) => (VOWEL.test(n) ? `d’${n}` : `de ${n}`);
export const shortLabel = (label: string) => label.trim().replace(/^(\d+)\s*secondes?$/i, '$1 s');
/** « Pas d'arbitre », « Pas de 24 s ». */
export const noneChip = (label: string) => cap(labelForms(shortLabel(label)).pas);

// -----------------------------------------------------------------------------
// Les jours (« samedi », « de demain », « du samedi 14 novembre ») : comme _vol_day / _vol_day_de en base
// -----------------------------------------------------------------------------
const WD = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const parse = (d: string) => { const [y, m, dd] = d.slice(0, 10).split('-').map(Number); return new Date(Date.UTC(y!, m! - 1, dd!)); };
/** « samedi 14 novembre », « dimanche 1er novembre » (comme fr_day). */
export const frDay = (d: string) => { const x = parse(d); return `${WD[x.getUTCDay()]} ${x.getUTCDate()}${x.getUTCDate() === 1 ? 'er' : ''} ${MONTHS[x.getUTCMonth()]}`; };
const diff = (a: string, b: string) => Math.round((parse(a).getTime() - parse(b).getTime()) / 86_400_000);

export function dayWords(day: string, today: string): { bare: string; de: string } {
  const n = diff(day, today);
  if (n === 0) return { bare: 'aujourd’hui', de: 'd’aujourd’hui' };
  if (n === 1) return { bare: 'demain', de: 'de demain' };
  if (n >= 2 && n <= 6) { const w = WD[parse(day).getUTCDay()]!; return { bare: w, de: `de ${w}` }; }
  return { bare: `le ${frDay(day)}`, de: `du ${frDay(day)}` };
}

// -----------------------------------------------------------------------------
// Les messages qui partent tout seuls
// -----------------------------------------------------------------------------
export type ThankReason = 'comble' | 'plus_besoin';

/** « Merci Karim ! 🙏 » : retiré par le bureau, le besoin finalement comblé (ou plus besoin du tout). Pas de points. */
export function thankMessage(first: string, label: string, reason: ThankReason, day: string, today: string, vous = false): { title: string; body: string } {
  const f = labelForms(label), d = dayWords(day, today);
  const title = `Merci ${first} ! 🙏`;
  if (reason === 'plus_besoin') return { title, body: `Finalement, ${f.pas} ${d.bare} : merci quand même !` };
  return { title, body: `Finalement, ${f.le} ${d.de} ${f.plural ? 'sont' : 'est'} déjà ${agreeNoun(label, 'assuré')} : on n’a plus besoin de ${vous ? 'vous' : 'toi'} cette fois. À bientôt au gymnase !` };
}
/** Chez les parents d'un mineur (D-103) : « Kenza n'est plus attendue à la table de marque samedi ». */
export function thankParentMessage(kid: string, kidFem: boolean, label: string, reason: ThankReason, day: string, today: string): { title: string; body: string } {
  const f = labelForms(label), d = dayWords(day, today);
  return {
    title: `${kid} n’est plus ${kidFem ? 'attendue' : 'attendu'} ${f.a} ${d.bare}`,
    body: reason === 'plus_besoin' ? `Finalement, ${f.pas} ${d.bare}. Merci quand même !`
      : `Finalement, ${f.le} ${d.de} ${f.plural ? 'sont' : 'est'} déjà ${agreeNoun(label, 'assuré')}. Merci quand même !`,
  };
}
/** « Le bureau t'a inscrite à la table de marque samedi » (la personne peut se désister). */
export function addedMessage(label: string, day: string, today: string, vous: boolean, fem: boolean): string {
  return `Le bureau ${vous ? 'vous a' : 't’a'} inscrit${fem ? 'e' : ''} ${labelForms(label).a} ${dayWords(day, today).bare}`;
}
/** Chez les parents : « Le bureau a inscrit Kenza à la table de marque samedi ». */
export const addedParentMessage = (kid: string, label: string, day: string, today: string) =>
  `Le bureau a inscrit ${kid} ${labelForms(label).a} ${dayWords(day, today).bare}`;

// -----------------------------------------------------------------------------
// Une équipe engagée : son nom, son niveau, ses puces, la suggestion
// -----------------------------------------------------------------------------
export interface Unit {
  team_id: string; squad_id: string | null; team: string; squad: string | null; category: string | null; gender: 'F' | 'M' | 'X';
  team_level: string | null; squad_level: string | null; squad_comp: string | null; team_comp: string | null;
  upcoming: number; hand: number;
}
export const unitKey = (u: { team_id: string; squad_id: string | null }) => `${u.team_id}:${u.squad_id ?? ''}`;
/** « Seniors filles · SF1 », « U15 filles ». */
export const unitName = (u: Pick<Unit, 'team' | 'squad'>) => (u.squad ? `${u.team} · ${u.squad}` : u.team);
/** « SF1 », « U15 filles » (dans une phrase). */
export const unitShort = (u: Pick<Unit, 'team' | 'squad'>) => u.squad ?? u.team;

/** L'ordre des équipes : seniors d'abord, puis des plus grands aux plus petits. */
export const unitRank = (u: Pick<Unit, 'category'>) => { const m = /U\s?(\d+)/i.exec(u.category ?? ''); return m ? Number(m[1]) : 99; };

const HINT_LEVELS: LevelKey[] = ['national', 'prenational', 'regional_seniors', 'regional_jeunes'];
const REF = /arbitr/i, SHOT = /^\s*24\s*s/i;
export const isMini = (key: LevelKey | null) => key === 'minibasket';
export type HintKind = 'arbitre' | 'mini';
/** La suggestion selon le niveau : seulement si le club a un besoin dont le libellé correspond, pas déjà « Aucun ». */
export function hintFor(key: LevelKey | null, club: ClubNeed[], rules: UnitRule[]): { kind: HintKind; labels: string[] } | null {
  const kind: HintKind | null = key === 'minibasket' ? 'mini' : key && HINT_LEVELS.includes(key) ? 'arbitre' : null;
  if (!kind) return null;
  const res = kind === 'mini' ? [REF, SHOT] : [REF];
  const labels = club.filter(c => res.some(r => r.test(c.label)) && ruleOf(rules, c.label)?.mode !== 'none').map(c => c.label);
  return labels.length ? { kind, labels } : null;
}
/** Les textes de la suggestion. */
export function hintText(kind: HintKind, unit: string, level: string | null, labels: string[]): { lead: string; ask: string; yes: string } {
  const what = labels.map(l => labelForms(l).pas.replace(/^pas /, '')).join(' ni ');
  return kind === 'mini'
    ? { lead: `${unit} joue en mini-basket : les jeunes arbitrent entre eux.`, ask: `Ne plus demander ${what} pour ses plateaux à domicile ?`, yes: `Oui, pour ${unit}` }
    : { lead: `${unit} joue en ${level ?? 'régional'} : la ligue désigne les arbitres.`, ask: `Ne plus demander ${what} pour les matchs à domicile ${deName(unit)} ?`, yes: `Oui, pour ${unit}` };
}
/** La règle que la suggestion applique (« Aucun » pour ces besoins), les autres règles gardées. */
export const applyHint = (rules: UnitRule[], labels: string[]): UnitRule[] => [
  ...rules.filter(r => r.extra || !labels.some(l => sameLabel(l, r.label))),
  ...labels.map((label, i) => ({ label, mode: 'none' as const, needed: null, only_multi: false, extra: false, position: 50 + i })),
];

/** « match » ou « plateau » (mini-basket). */
export const matchWord = (mini: boolean, n = 1) => (mini ? (n > 1 ? 'plateaux' : 'plateau') : (n > 1 ? 'matchs' : 'match'));
/** « 9 matchs à domicile à venir », « aucun plateau à domicile à venir ». */
export const homeCount = (n: number, mini: boolean) => `${n ? n : 'aucun'} ${matchWord(mini, n)} à domicile à venir`;

/** Pourquoi une règle (sous le besoin, dans la fiche de l'équipe). */
export function ruleWhy(label: string, mode: RuleMode, onlyMulti: boolean, key: LevelKey | null, level: string | null): string {
  if (mode === 'none' && REF.test(label) && key && HINT_LEVELS.includes(key)) return `Désignés par la ligue en ${level ?? 'régional'}.`;
  if (mode === 'none' && REF.test(label) && isMini(key)) return 'Les jeunes arbitrent entre eux en mini-basket.';
  if (mode !== 'none' && onlyMulti) {
    const f = labelForms(label), w = matchWord(isMini(key));
    return `Un ${w} seul au gymnase : ${f.pas}. Deux matchs ou plus ce jour-là : ${f.le} de ce ${w} ${f.plural ? 'sont' : 'est'} ${agreeNoun(label, 'demandé')}.`;
  }
  return '';
}

/** Les puces qui résument les règles d'une équipe ; sans règle : « Comme le club ». */
export function ruleChips(club: ClubNeed[], rules: UnitRule[]): string[] {
  const set: string[] = [], none: string[] = [];
  for (const c of club) {
    const r = ruleOf(rules, c.label);
    if (!r || r.extra) continue;
    const L = shortLabel(c.label);
    if (r.mode === 'none') none.push(noneChip(c.label));
    else if (r.mode === 'count') set.push(`${L} : ${r.needed}${r.only_multi ? ', si 2 matchs ou plus' : ''}`);
    else if (r.only_multi) set.push(`${L} : si 2 matchs ou plus`);
  }
  const extra = rules.filter(r => r.extra && !club.some(c => sameLabel(c.label, r.label)))
    .map(r => `＋ ${shortLabel(r.label)} : ${r.needed ?? 1}${r.only_multi ? ', si 2 matchs ou plus' : ''}`);
  return [...set, ...none, ...extra];
}

/** Ce qui change pour une équipe, d'un réglage à l'autre : « Arbitre retiré, 24 secondes passées à 2. » */
export function changeSummary(club: ClubNeed[], before: UnitRule[], after: UnitRule[]): string {
  const a = planFor(club, before, true), b = planFor(club, after, true);
  const keys = [...new Set([...a, ...b].map(p => norm(p.label)))];
  const parts: string[] = [];
  for (const k of keys) {
    const x = a.find(p => norm(p.label) === k), y = b.find(p => norm(p.label) === k);
    const label = (y ?? x)!.label;
    const was = !!x?.wanted, is = !!y?.wanted;
    if (was && !is) parts.push(`${label} ${agreeNoun(label, 'retiré')}`);
    else if (!was && is) parts.push(`${label} ${agreeNoun(label, 'ajouté')}${y!.needed > 1 ? ` (${y!.needed})` : ''}`);
    else if (is) {
      if (x!.needed !== y!.needed) parts.push(`${label} ${agreeNoun(label, 'passé')} à ${y!.needed}`);
      if (x!.conditional !== y!.conditional) parts.push(`${label} ${y!.conditional ? 'seulement s’il y a 2 matchs ou plus' : 'à chaque match'}`);
    }
  }
  return parts.length ? `${cap(parts.join(', '))}.` : '';
}

// -----------------------------------------------------------------------------
// Ce qu'une personne fait déjà ce jour-là (fiche « Inscrire quelqu'un »)
// -----------------------------------------------------------------------------
export interface Busy {
  kind: 'play' | 'coach' | 'drive' | 'slot'; event_kind: string | null; team: string | null; opponent: string | null;
  away: boolean | null; starts_at: string | null; label: string | null; same: boolean;
}
const hhmm = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`; };
const partOfDay = (iso: string | null) => { if (!iso) return 'ce jour-là'; const h = new Date(iso).getHours(); return h >= 18 ? 'ce soir' : h < 12 ? 'ce matin' : 'cet après-midi'; };
const vs = (b: Busy) => (b.opponent ? `${b.away ? 'à' : 'contre'} ${b.opponent}` : '');

/** « Déjà à la buvette ce soir », « Joue ce jour-là à 13h00 (SF2 à Lusmagne) » ; `warn` : un match, une séance, la voiture. */
export function busyLine(b: Busy): { text: string; warn: boolean } {
  const at = b.starts_at ? ` à ${hhmm(b.starts_at)}` : '';
  const match = b.event_kind === 'match';
  switch (b.kind) {
    case 'slot': return { text: `Déjà ${labelForms(b.label ?? 'créneau').a.replace(/^comme /, '')} ${partOfDay(b.starts_at)}`, warn: false };
    case 'play': return { text: b.same ? 'Joue ce match' : match ? `Joue ce jour-là${at} (${[b.team, vs(b)].filter(Boolean).join(' ')})` : `Entraînement ce jour-là${at} (${b.team ?? ''})`, warn: true };
    case 'coach': return { text: b.same ? 'Coach de ce match' : match ? `Coach ce jour-là${at} (${[b.team, vs(b)].filter(Boolean).join(' ')})` : `Entraîne ce jour-là${at} (${b.team ?? ''})`, warn: true };
    default: return { text: b.same ? 'Conduit à ce match' : `Conduit ce jour-là${at} (${[b.team, vs(b)].filter(Boolean).join(' ')})`, warn: true };
  }
}

/** « Karim y est inscrit », « Karim et Claire y sont inscrits » ; le bouton « Retirer et prévenir Karim ». */
export function keptWho(people: { name: string; g: string }[]): { line: string; action: string } {
  const first = (n: string) => n.trim().split(/\s+/)[0] ?? n;
  const names = people.map(p => first(p.name));
  const allF = people.length > 0 && people.every(p => p.g === 'F');
  if (people.length <= 1) return { line: `${names[0] ?? 'Quelqu’un'} y est ${allF ? 'inscrite' : 'inscrit'}`, action: `Retirer et prévenir ${names[0] ?? 'la personne'}` };
  if (people.length === 2) return { line: `${names[0]} et ${names[1]} y sont ${allF ? 'inscrites' : 'inscrits'}`, action: `Retirer et prévenir ${names[0]} et ${names[1]}` };
  return { line: `${names[0]}, ${names[1]} et ${people.length - 2} autres y sont ${allF ? 'inscrites' : 'inscrits'}`, action: `Retirer et prévenir les ${people.length} ${allF ? 'inscrites' : 'inscrits'}` };
}
